import { Component, inject, input, OnInit, output, ViewChild } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MicrosoftEntraAttributesResource } from '../../../../../../shared/resources/microsoft-entra-attributes.resource';
import { ClientErrorReason, MicrosoftAttributes } from '../../../../../../../shared/models/generated/ziroModels';
import { ZiroFieldConfigs, ZiroFormConfig, ZiroFormsUpdate } from '../../../../../../../forms/ziro-forms-models';
import { HtmlInputType, ZiroTextConfig } from '../../../../../../../forms/fields/text/ziro-text.component';
import {
  HelpdeskEntraAttributesEntity,
  HelpdeskEntraAttributesFormComponent,
} from './helpdesk-entra-attributes-form/helpdesk-entra-attributes-form.component';
import { ZiroIcons } from '../../../../../../../shared/models/ziro-icons.enum';

@Component({
  selector: 'ziro-helpdesk-entra-attributes',
  templateUrl: './helpdesk-entra-attributes.component.html',
  standalone: false,
})
export class HelpdeskEntraAttributesComponent implements OnInit {
  private _microsoftEntraAttributesResource = inject(MicrosoftEntraAttributesResource);

  @ViewChild(HelpdeskEntraAttributesFormComponent) entraAttributeForm: HelpdeskEntraAttributesFormComponent;
  entraItemWasChanged = output<void>();
  tenantId = input<string>();
  upn = input<string>();
  dialPlanId = input<number>();
  mainNumber = input<string>();
  selectedNumber = input<string>();
  lineUriExtensionForDedicatedDid = input<string>();
  isExistingTeamsCalling = input<boolean>();

  initialEntity: HelpdeskEntraAttributesEntity;
  isLoading = true;
  formConfig: ZiroFormConfig;
  attributeFields: string[] = [];
  ziroIcons = ZiroIcons;
  isAnyAttributeVisible = true;
  isPermissionMissing = false;

  ngOnInit(): void {
    this.init();
  }

  init(manuallyUpdatedExtensionValue?: string): void {
    this.isLoading = true;
    this.isPermissionMissing = false;
    const mainNumber = this.mainNumber();
    const lineUriExtensionForDedicatedDid = this.lineUriExtensionForDedicatedDid();
    const fieldConfig$ = this._microsoftEntraAttributesResource.getEntraAttributesFieldConfig(
      this.tenantId(),
      mainNumber ? mainNumber : this.selectedNumber(),
      lineUriExtensionForDedicatedDid
        ? manuallyUpdatedExtensionValue || lineUriExtensionForDedicatedDid
        : this.mainNumber()
          ? this.selectedNumber()
          : '',
    );

    const existingAttributes$ = this.isExistingTeamsCalling()
      ? this._microsoftEntraAttributesResource.getEntraAttributes(this.tenantId(), this.upn(), this.dialPlanId())
      : of(null);
    forkJoin([fieldConfig$, existingAttributes$])
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.isPermissionMissing = error.error?.reason !== ClientErrorReason.USER_IS_NOT_CLOUD_ONLY;
          this.isAnyAttributeVisible = false;
          return of(null);
        }),
      )
      .subscribe((result) => {
        if (result) {
          const [fieldConfig, existingAttributes] = result;
          const existingAttributeValues = existingAttributes ? existingAttributes.writeDownListJson : [];
          this.attributeFields = Object.keys(fieldConfig.attributes);
          this.formConfig = {
            fields: this.attributeFields.reduce((acc: ZiroFieldConfigs, name) => {
              acc[name] = {
                label: name,
                required: fieldConfig.attributes[name].required,
                hidden: () => !fieldConfig.attributes[name].show,
                dataAutomation: name + '-input',
                defaultValue: () => fieldConfig.attributes[name].defaultValue,
                componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
              };
              return acc;
            }, {}),
          };
          // If all fields are hidden, initialEntity will be used to ensure we are saving the correct values
          this.initialEntity = this.attributeFields.reduce((acc: HelpdeskEntraAttributesEntity, name) => {
            const existingValue = existingAttributeValues.find(
              (attr) => attr.attributeName === name && !!attr.attributeValue,
            );
            acc[name] = existingValue ? existingValue.attributeValue : fieldConfig.attributes[name].defaultValue;
            return acc;
          }, {});
          this.isAnyAttributeVisible = Object.entries(fieldConfig.attributes).some(
            (attr) =>
              attr[1].show ||
              Object.entries(this.initialEntity).some(
                (entry) => entry[0] === attr[0] && entry[1] !== attr[1].defaultValue,
              ),
          );
        }
        this.isLoading = false;
      });
  }

  onChildFormUpdate(event: ZiroFormsUpdate<HelpdeskEntraAttributesEntity>): void {
    if (event.new !== event.old) {
      this.entraItemWasChanged.emit();
    }
  }

  isFormValid(): boolean {
    if (this.isPermissionMissing || !this.isAnyAttributeVisible) {
      return true;
    } else {
      return !!this.entraAttributeForm && this.entraAttributeForm?.isFormValid();
    }
  }

  submitEntraAttributeForm(): Observable<void> {
    if (this.isPermissionMissing) {
      return of(null);
    }
    return !!this.entraAttributeForm || !!this.initialEntity
      ? this._microsoftEntraAttributesResource
          .putEntraAttributes(
            this.tenantId(),
            this.upn(),
            this.dialPlanId(),
            this._getPayload(this.entraAttributeForm ? this.entraAttributeForm.entity : this.initialEntity, this.upn()),
          )
          .pipe(
            catchError((error: HttpErrorResponse) =>
              error.error?.reason === ClientErrorReason.USER_IS_NOT_CLOUD_ONLY ? of(null) : throwError(() => error),
            ),
          )
      : of(null);
  }

  private _getPayload(formData: HelpdeskEntraAttributesEntity, userPrincipalName: string): MicrosoftAttributes {
    return {
      userPrincipalName,
      writeDownListJson: Object.entries(formData).map((entry) => {
        return {
          attributeName: entry[0],
          attributeValue: entry[1],
        };
      }),
    };
  }
}
